import { useState } from 'react'
import { addDays, format, startOfWeek } from 'date-fns'
import { useAuth } from '../../../hooks/useAuth'
import { useLinkedHouseholds } from '../../linkedHouseholds/hooks/useLinkedHouseholds'
import { useSharedMembers } from '../../linkedHouseholds/hooks/useSharedMembers'
import { useCustodySchedules } from '../hooks/useCustody'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const SCHEDULE_TYPES = [
  {
    id: 'alternating_weeks',
    label: 'Alternating Weeks',
    desc: 'One full week with each household, switching every 7 days.',
    preview: [0,0,0,0,0,0,0,1,1,1,1,1,1,1],
  },
  {
    id: 'two_two_three',
    label: '2-2-3 Rotation',
    desc: 'Mon-Tue with one household, Wed-Thu with the other, Fri-Sun alternates weekly.',
    preview: [0,0,1,1,0,0,0,1,1,0,0,1,1,1],
  },
  {
    id: 'two_two_five_five',
    label: '2-2-5-5 Rotation',
    desc: '2 days, 2 days, 5 days, 5 days — repeating 14-day cycle.',
    preview: [0,0,1,1,0,0,0,0,0,1,1,1,1,1],
  },
  {
    id: 'every_weekend',
    label: 'Every Weekend',
    desc: 'One household gets Friday–Sunday, the other gets Monday–Thursday.',
    preview: [0,1,1,1,1,0,0,0,1,1,1,1,0,0],
  },
  {
    id: 'custom',
    label: 'Custom Days',
    desc: 'Choose exactly which days of the week each household gets.',
    preview: null,
  },
]

const COLORS = [
  '#6366f1','#ec4899','#f59e0b','#10b981','#3b82f6',
  '#8b5cf6','#ef4444','#14b8a6','#f97316','#84cc16',
]

const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

// Mini 14-day preview strip
const SchedulePreview = ({ preview, colorA, colorB }) => (
  <div className="flex gap-0.5 mt-2">
    {preview.map((who, i) => (
      <div
        key={i}
        className="flex-1 h-5 rounded-sm"
        style={{ backgroundColor: who === 0 ? colorA : colorB, opacity: 0.8 }}
        title={`Day ${i + 1}`}
      />
    ))}
  </div>
)

export const ScheduleWizard = ({ onClose }) => {
  const { household, user } = useAuth()
  const { activeLink }       = useLinkedHouseholds()
  const { sharedOut, sharedIn } = useSharedMembers()
  const { create }           = useCustodySchedules()

  const linkedHouseholdId = activeLink
    ? (activeLink.primary_household_id === household?.id
        ? activeLink.linked_household_id
        : activeLink.primary_household_id)
    : null
  const linkedHouseholdName = activeLink?.otherHousehold?.name ?? 'Other House'

  const allShared = [...sharedOut, ...sharedIn]

  const [step, setStep] = useState(1)
  // Step 1
  const [childId,       setChildId]       = useState(allShared[0]?.user_id ?? '')
  const [scheduleName,  setScheduleName]  = useState('')
  const [color,         setColor]         = useState(COLORS[0])
  // Step 2
  const [scheduleType,  setScheduleType]  = useState('alternating_weeks')
  // Step 3
  const [startDate,     setStartDate]     = useState(format(new Date(), 'yyyy-MM-dd'))
  const [firstHousehold, setFirstHousehold] = useState('ours')
  // Custom type state
  const [customDays, setCustomDays] = useState({ 0:'ours',1:'ours',2:'ours',3:'ours',4:'ours',5:'other',6:'other' })

  const [saving, setSaving] = useState(false)
  const [error,  setError]  = useState('')

  const selectedChild = allShared.find((s) => s.user_id === childId)
  const ourHouseholdName   = household?.name ?? 'Your House'

  const toggleCustomDay = (dow) => {
    setCustomDays((prev) => ({ ...prev, [dow]: prev[dow] === 'ours' ? 'other' : 'ours' }))
  }

  const buildRules = () => {
    if (scheduleType === 'custom') {
      return Object.entries(customDays).map(([dow, who]) => ({
        rule_type:                'fixed_days',
        day_of_week:              parseInt(dow),
        assigned_to_household_id: who === 'ours' ? household.id : linkedHouseholdId,
      }))
    }
    const assignedFirst = firstHousehold === 'ours' ? household.id : linkedHouseholdId
    const blockLen = scheduleType === 'two_two_five_five' ? 14
                   : scheduleType === 'every_weekend'    ? 7
                   : scheduleType === 'two_two_three'    ? 7
                   : 7
    return [{
      rule_type:                'repeating_block',
      block_length_days:        blockLen,
      block_start_date:         startDate,
      assigned_to_household_id: assignedFirst,
    }]
  }

  // 4-week preview rows
  const previewWeeks = () => {
    const weeks = []
    let cur = new Date(startDate)
    // Align to Sunday start
    cur = startOfWeek(cur, { weekStartsOn: 0 })
    for (let w = 0; w < 4; w++) {
      const week = []
      for (let d = 0; d < 7; d++) {
        week.push({ date: new Date(cur), dow: cur.getDay() })
        cur = addDays(cur, 1)
      }
      weeks.push(week)
    }
    return weeks
  }

  const getPreviewHousehold = (date) => {
    const start = new Date(startDate)
    const diff  = Math.floor((date - start) / 86400000)
    if (diff < 0) return 'ours'
    const first = firstHousehold

    if (scheduleType === 'alternating_weeks') {
      const block = Math.floor(diff / 7) % 2
      return (block === 0 ? first : (first === 'ours' ? 'other' : 'ours'))
    }
    if (scheduleType === 'two_two_three') {
      const weekParity = Math.floor(diff / 7) % 2
      const dayInWeek  = diff % 7
      if (dayInWeek <= 1) return weekParity === 0 ? first : (first === 'ours' ? 'other' : 'ours')
      if (dayInWeek <= 3) return weekParity === 0 ? (first === 'ours' ? 'other' : 'ours') : first
      return weekParity === 0 ? first : (first === 'ours' ? 'other' : 'ours')
    }
    if (scheduleType === 'two_two_five_five') {
      const pos = diff % 14
      if (pos <= 1)  return first
      if (pos <= 3)  return first === 'ours' ? 'other' : 'ours'
      if (pos <= 8)  return first
      return first === 'ours' ? 'other' : 'ours'
    }
    if (scheduleType === 'every_weekend') {
      const dow = date.getDay()
      const weekendOwner = first
      return (dow === 0 || dow === 5 || dow === 6) ? weekendOwner : (weekendOwner === 'ours' ? 'other' : 'ours')
    }
    if (scheduleType === 'custom') {
      const dow = date.getDay()
      return customDays[dow]
    }
    return 'ours'
  }

  const handleSave = async () => {
    if (!childId)        { setError('Select a child.'); return }
    if (!scheduleName.trim()) { setError('Enter a schedule name.'); return }
    setSaving(true)
    setError('')
    try {
      const rules = buildRules()
      await create.mutateAsync({
        schedule: {
          household_id:        household.id,
          linked_household_id: linkedHouseholdId,
          child_id:            childId,
          schedule_type:       scheduleType,
          schedule_name:       scheduleName.trim(),
          color,
          start_date:          startDate,
          is_active:           true,
          created_by:          user.id,
        },
        rules,
      })
      onClose()
    } catch (err) {
      setError(err.message ?? 'Save failed.')
      setSaving(false)
    }
  }

  const inputCls = 'w-full rounded-xl border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
  const lbl = (t) => <label className="block text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">{t}</label>

  return (
    <Portal>
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-text-primary">New Custody Schedule</h2>
            <p className="text-xs text-text-muted mt-0.5">Step {step} of 4</p>
          </div>
          <button type="button" onClick={onClose} className="text-text-muted p-1">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Step progress */}
        <div className="flex gap-1 px-5 py-2 flex-shrink-0">
          {[1,2,3,4].map((s) => (
            <div key={s} className={cn('flex-1 h-1 rounded-full transition-colors', s <= step ? 'bg-primary' : 'bg-border')} />
          ))}
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4 space-y-4">
          {/* ── Step 1: Child & name ── */}
          {step === 1 && (
            <>
              <div>
                {lbl('Child')}
                {allShared.length === 0 ? (
                  <p className="text-sm text-text-muted bg-background rounded-xl px-4 py-3">
                    No shared children found. Share a child from Household Settings first.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allShared.map((sm) => (
                      <button
                        key={sm.user_id}
                        type="button"
                        onClick={() => setChildId(sm.user_id)}
                        className={cn(
                          'px-4 py-2 rounded-full text-sm font-medium border transition-colors',
                          childId === sm.user_id
                            ? 'bg-primary text-white border-primary'
                            : 'border-border text-text-secondary bg-surface',
                        )}
                      >
                        {sm.profiles?.display_name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                {lbl('Schedule Name')}
                <input
                  type="text"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  placeholder={`${selectedChild?.profiles?.display_name ?? 'Child'}'s Schedule`}
                  className={inputCls}
                />
              </div>

              <div>
                {lbl('Color')}
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={cn(
                        'w-8 h-8 rounded-full border-2 transition-transform',
                        color === c ? 'border-text-primary scale-110' : 'border-transparent',
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Schedule type ── */}
          {step === 2 && (
            <div className="space-y-3">
              {SCHEDULE_TYPES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setScheduleType(t.id)}
                  className={cn(
                    'w-full text-left p-4 rounded-xl border transition-colors',
                    scheduleType === t.id ? 'border-primary bg-primary/5' : 'border-border bg-surface',
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      'w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5',
                      scheduleType === t.id ? 'border-primary bg-primary' : 'border-border',
                    )} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-text-primary">{t.label}</p>
                      <p className="text-xs text-text-muted mt-0.5">{t.desc}</p>
                      {t.preview && scheduleType === t.id && (
                        <SchedulePreview preview={t.preview} colorA={color} colorB="#e8e4f0" />
                      )}
                    </div>
                  </div>
                </button>
              ))}

              {scheduleType === 'custom' && (
                <div className="bg-background rounded-xl p-4">
                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">Assign days</p>
                  <div className="grid grid-cols-7 gap-1">
                    {DAY_LABELS.map((day, dow) => (
                      <button
                        key={dow}
                        type="button"
                        onClick={() => toggleCustomDay(dow)}
                        className="flex flex-col items-center gap-1"
                      >
                        <span className="text-xs text-text-muted">{day}</span>
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold text-white"
                          style={{ backgroundColor: customDays[dow] === 'ours' ? color : '#e8e4f0',
                                   color: customDays[dow] === 'ours' ? 'white' : '#7c6faf' }}
                        >
                          {customDays[dow] === 'ours' ? 'Y' : 'O'}
                        </div>
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-text-muted">
                    <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1" style={{ backgroundColor: color }} />Yours</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-border mr-1" />Other</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Start date & anchor ── */}
          {step === 3 && (
            <>
              <div>
                {lbl('Start Date')}
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className={inputCls}
                />
              </div>

              {scheduleType !== 'custom' && (
                <div>
                  {lbl('First Block Goes To')}
                  <div className="flex gap-2">
                    {[
                      { id: 'ours',  label: ourHouseholdName },
                      { id: 'other', label: linkedHouseholdName },
                    ].map(({ id, label }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setFirstHousehold(id)}
                        className={cn(
                          'flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors',
                          firstHousehold === id ? 'bg-primary text-white border-primary' : 'border-border text-text-secondary',
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 4-week preview */}
              <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Preview (4 weeks)</p>
                <div className="bg-background rounded-xl p-3 overflow-x-auto">
                  <div className="grid grid-cols-7 gap-0.5 w-full">
                    {DAY_LABELS.map((d) => (
                      <div key={d} className="text-center text-xs text-text-muted py-1">{d}</div>
                    ))}
                    {previewWeeks().flatMap((week) =>
                      week.map(({ date, dow }) => {
                        const who = getPreviewHousehold(date)
                        return (
                          <div
                            key={date.toISOString()}
                            className="rounded text-center py-1.5 text-xs font-medium"
                            style={{
                              backgroundColor: who === 'ours' ? color + '33' : '#e8e4f0',
                              color: who === 'ours' ? color : '#7c6faf',
                              border: `1px solid ${who === 'ours' ? color + '66' : '#e8e4f0'}`,
                            }}
                          >
                            {format(date, 'd')}
                          </div>
                        )
                      })
                    )}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-text-muted">
                    <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1" style={{ backgroundColor: color + '66' }} />{ourHouseholdName}</span>
                    <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-border mr-1" />{linkedHouseholdName}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 4: Confirm ── */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-background rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{scheduleName}</p>
                    <p className="text-xs text-text-muted">{selectedChild?.profiles?.display_name}</p>
                  </div>
                </div>
                <div className="text-sm text-text-secondary space-y-1">
                  <p>Type: <strong>{SCHEDULE_TYPES.find((t) => t.id === scheduleType)?.label}</strong></p>
                  <p>Starting: <strong>{format(new Date(startDate), 'MMMM d, yyyy')}</strong></p>
                  <p>Households: <strong>{ourHouseholdName}</strong> ↔ <strong>{linkedHouseholdName}</strong></p>
                </div>
              </div>
              <p className="text-xs text-text-muted">
                Both households will see this custody calendar. Either household admin can add exceptions.
              </p>
              {error && <p className="text-sm text-rose-600">{error}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex gap-2 flex-shrink-0">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              className="flex-1 border border-border rounded-xl py-3 text-sm font-medium text-text-secondary"
            >
              Back
            </button>
          )}
          {step < 4 ? (
            <button
              type="button"
              onClick={() => {
                if (step === 1 && (!childId || !scheduleName.trim())) {
                  setError('Fill in all required fields.')
                  return
                }
                setError('')
                setStep((s) => s + 1)
              }}
              className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-primary text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <><Spinner size="sm" /> Saving…</> : 'Create Schedule'}
            </button>
          )}
        </div>
      </div>
    </div>
    </Portal>
  )
}
